import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Header from '../../../components/header';
import Layout from '../../../components/layout';
import { AgGridReact } from 'ag-grid-react';
import Icon from '../../../components/icons';
import Drawer from '../../../components/Drawer';
import { Dropdown, Menu } from '../../../components/Dropdown';
import Button from '../../../components/elements/button';
import { DATE_FORMAT, PAGE_SIZE } from '../../../_common/constant';
import Pagination from '../../../components/pagination';
import Search from '../../../components/search';
import { downloadExcel, downloadPDF, showLocalDate } from '../../../_common/functions';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import toast from 'react-hot-toast';
import { useStockAdjustmentApi } from '../../../store/api/hooks/stockAdjustmentApiHook';
import { useSpearPartsApi } from '../../../store/api/hooks/spearPartsApiHook';
import { useFacilitiesApi } from '../../../store/api/hooks/facilitiesApiHook';
import Currency from '../../../components/currency';
import moment from 'moment';
import { filterConfigType } from '../../../_config/types';
import Filters from '../../../components/filters';
import { ImUsers } from "react-icons/im";
import { MdMapsHomeWork } from "react-icons/md";

// Facility Stock Cell Renderer with hover popup
const FacilityStockCellRenderer: React.FC<{ data: any; value: any }> = ({ data, value }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
    const iconRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const popupMaxHeight = 200;

    const clearHideTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const handleMouseEnter = () => {
        clearHideTimeout();
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;

            if (spaceBelow >= popupMaxHeight + 10) {
                setPopupStyle({
                    top: rect.bottom + 5,
                    left: rect.left - 100,
                    bottom: 'auto'
                });
            } else {
                setPopupStyle({
                    bottom: windowHeight - rect.top + 5,
                    left: rect.left - 100,
                    top: 'auto'
                });
            }
        }
        setShowPopup(true);
    };

    const handleMouseLeave = () => {
        clearHideTimeout();
        timeoutRef.current = setTimeout(() => {
            setShowPopup(false);
        }, 100);
    };

    const handlePopupMouseEnter = () => {
        clearHideTimeout();
    };

    const handlePopupMouseLeave = () => {
        clearHideTimeout();
        timeoutRef.current = setTimeout(() => {
            setShowPopup(false);
        }, 100);
    };

    const facilityData: { name: string; count: number }[] = data?.facilityStockDetails || [];

    return (
        <div className="flex items-center justify-end gap-2">
            <span>{value !== null && value !== undefined ? value : '--'}</span>
            <div
                ref={iconRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="cursor-pointer relative"
            >
                <MdMapsHomeWork className="text-gray-600 hover:text-blue-600" />
                {showPopup && createPortal(
                    <div
                        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] overflow-y-auto"
                        style={{ 
                            ...popupStyle,
                            minWidth: '200px',
                            maxHeight: `${popupMaxHeight}px`
                        }}
                        onMouseEnter={handlePopupMouseEnter}
                        onMouseLeave={handlePopupMouseLeave}
                    >
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-1 px-2 font-semibold text-gray-700">Facility Name</th>
                                    <th className="text-right py-1 px-2 font-semibold text-gray-700">Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {facilityData.length === 0 ? (
                                    <tr><td colSpan={2} className="py-2 px-2 text-center text-gray-400 text-xs">No facility stock</td></tr>
                                ) : facilityData.map((facility: any, index: number) => (
                                    <tr key={index} className="border-b border-gray-100 last:border-0">
                                        <td className="py-1 px-2 text-gray-600">{facility.facilityName}</td>
                                        <td className="py-1 px-2 text-right text-gray-600">{facility.stock}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
};

// Personal Stock Cell Renderer with hover popup
const PersonalStockCellRenderer: React.FC<{ data: any; value: any }> = ({ data, value }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
    const iconRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const popupMaxHeight = 200; // max-height of popup

    const clearHideTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const handleMouseEnter = () => {
        clearHideTimeout();
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;

            // Check if there's enough space below, otherwise open above
            if (spaceBelow >= popupMaxHeight + 10) {
                // Open below
                setPopupStyle({
                    top: rect.bottom + 5,
                    left: rect.left - 100,
                    bottom: 'auto'
                });
            } else {
                // Open above - use bottom positioning so it sits right above the icon
                setPopupStyle({
                    bottom: windowHeight - rect.top + 5,
                    left: rect.left - 100,
                    top: 'auto'
                });
            }
        }
        setShowPopup(true);
    };

    const handleMouseLeave = () => {
        clearHideTimeout();
        timeoutRef.current = setTimeout(() => {
            setShowPopup(false);
        }, 100);
    };

    const handlePopupMouseEnter = () => {
        clearHideTimeout();
    };

    const handlePopupMouseLeave = () => {
        clearHideTimeout();
        timeoutRef.current = setTimeout(() => {
            setShowPopup(false);
        }, 100);
    };

    const peopleData: { name: string; count: number }[] = data?.personalStockDetails || [];

    return (
        <div className="flex items-center justify-end gap-2">
            <span>{value !== null && value !== undefined ? value : '--'}</span>
            <div
                ref={iconRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="cursor-pointer relative"
            >
                <ImUsers className="text-gray-600 hover:text-blue-600" />
                {showPopup && createPortal(
                    <div
                        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] overflow-y-auto"
                        style={{ 
                            ...popupStyle,
                            minWidth: '200px',
                            maxHeight: `${popupMaxHeight}px`
                        }}
                        onMouseEnter={handlePopupMouseEnter}
                        onMouseLeave={handlePopupMouseLeave}
                    >
                        <table className="w-full text-sm ">
                            <thead className="sticky top-0 bg-white">
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-1 px-2 font-semibold text-gray-700">People Name</th>
                                    <th className="text-right py-1 px-2 font-semibold text-gray-700">Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {peopleData.length === 0 ? (
                                    <tr><td colSpan={2} className="py-2 px-2 text-center text-gray-400 text-xs">No personal stock</td></tr>
                                ) : peopleData.map((person: any, index: number) => (
                                    <tr key={index} className="border-b border-gray-100 last:border-0">
                                        <td className="py-1 px-2 text-gray-600">{person.name}</td>
                                        <td className="py-1 px-2 text-right text-gray-600">{person.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>,
                    document.body
                )}
            </div>
            
        </div>
    );
};

const StockInventoryBalanceReportList: React.FC = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchParam, setSearchParam] = useState<string>('');
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [rowData, setrowData] = useState<any>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const stockAdjustmentAPI = useStockAdjustmentApi();
    const spearPartsAPI = useSpearPartsApi();
    const facilityApi = useFacilitiesApi();
    const [facilityoption, setFacilityoption] = useState<any>(null);

    // Filter states
    const filtersRef: any = useRef();
    const [filters, setFilters] = useState<any>({});
    const [filtersApplied, setFiltersApplied] = useState<any>({ generalFilter: false });
    const [filtersInitialized, setFiltersInitialized] = useState<boolean>(false);

    // Load facility list
    const getFacilityList = () => {
        facilityApi.getFacilityList({}, (resp: any) => {
            let facilitylist: any = [];
            resp.facilityList.map((facility: any) => {
                facilitylist.push({ label: facility.facilityName, value: facility.facilityID, data: facility });
            });
            setFacilityoption(facilitylist);
        }, (error: any) => {
            console.log("error =>", error);
            toast.error(error);
        });
    };

    // Load filters from localStorage on component mount
    useEffect(() => {
        getFacilityList();
        
        const savedFilters = localStorage.getItem('stockInventoryBalanceReportFilters');
        if (savedFilters) {
            try {
                const parsedFilters = JSON.parse(savedFilters);
                setFilters(parsedFilters);
            } catch (error) {
                console.error('Error parsing saved filters:', error);
                setFilters({});
            }
        } else {
            setFilters({});
        }
        setFiltersInitialized(true);
    }, []);

    // Save filters to localStorage when filters change
    useEffect(() => {
        if (Object.keys(filters).length > 0) {
            localStorage.setItem('stockInventoryBalanceReportFilters', JSON.stringify(filters));
        }
    }, [filters]);

    // Async function to load spare parts for Part Code filter
    const loadSparePartsOptions = async (searchParam: string): Promise<any> => {
        return new Promise((resolve) => {
            const payload = {
                pageSize: 50, // Limit results for dropdown
                pageNumber: 1,
                searchParam: searchParam
            };
            
            spearPartsAPI.getSpearPartsList(
                payload,
                (response: any) => {
                    const options = response.sparePartsList?.map((part: any) => ({
                        label: `${part.partsCode} - ${part.sparePartsName}`,
                        value: part.partsID
                    })) || [];
                    resolve(options);
                },
                (error: any) => {
                    console.error('Error loading spare parts:', error);
                    resolve([]);
                }
            );
        });
    };

    // Filter configuration
    const filterConfig: filterConfigType[] = [
        { 
            name: 'Part Code', 
            type: 'dropdown', 
            key: 'partIDs',
            async: loadSparePartsOptions
        },
        { name: 'Facility', type: 'dropdown', key: 'facilityIDs', options: facilityoption ? facilityoption : [] },
        { name: 'Till Date', type: 'date', key: 'tillDate' }
    ];

    const onGridReady = (params: any) => {
    };
    const onRowClicked = (event: any) => {
        if (!event.event.target.closest('input[type="checkbox"]')) {
            event.node.setSelected(false);
        }
    };

    const columnDefs = [
        { headerName: 'Date', field: 'date', headerClass: 'bg-[#f0f0f0]', width: 140,
            cellRenderer: (data: any) => data.value ? showLocalDate(data.value) : '--',
            headerTooltip: 'Date'
        },
        { headerName: 'Part Code', field: 'partCode', headerClass: 'bg-[#f0f0f0]', width: 200, cellRenderer: titleCell, headerTooltip: 'Part Code' },
        { headerName: 'Part Name', field: 'partName', headerClass: 'bg-[#f0f0f0]', minWidth: 150,flex:1,cellRenderer: titleCell, headerTooltip: 'Part Name' },
        { headerName: 'Facility Names', field: 'facilityNames', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Facility Names' },
        
        { headerName: 'Current Stock', field: 'currentStock',pinned: 'right', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 135, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: FacilityStockCellRenderer,
            headerTooltip: 'Current Stock' 
        },
         { headerName: 'Personal Stock', field: 'personalStock', pinned: 'right', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 135,
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: PersonalStockCellRenderer,
            headerTooltip: 'Personal Stock'
        },
        { headerName: 'Avg. Unit Cost', field: 'avgUnitCost', pinned: 'right', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 170, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Average Unit Cost' 
        },
        { headerName: 'Total Stock Value', field: 'totalStockValue', pinned: 'right', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 170, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Total Stock Value' 
        },
        
    ];

    const GetStockInventoryBalanceReportList = () => {
        let payload: any = {
            pageSize: PAGE_SIZE,
            pageNumber: currentPage,
            searchParam: searchParam,
        };

        // Add filters to payload
        if (filters) {
            Object.keys(filters).map((key: any) => {
                if (filters[key]) {
                    payload[key] = filters[key];
                }
            });
        }

        setrowData(null);

        const facilityIDs = filters?.facilityIDs ?? null;

        spearPartsAPI.getCurrentStockDetails(payload, (resp: any) => {
            const stockReportList = resp.sparePartsStockReportList ?? [];
            setTotalRecords(resp.totalRecord ?? stockReportList.length);

            // Fetch personal stock and merge into rows
            spearPartsAPI.getPersonalStockReport(
                {
                    facilityIDs,
                    partIDs: filters?.partIDs ?? null,
                    tillDate: filters?.tillDate ?? null,
                },
                (personalResp: any) => {
                    const personalList: any[] = personalResp?.personalStockList ?? [];
                    // Build map keyed by lowercase partCode
                    const personalMap: Record<string, { name: string; count: number }[]> = {};
                    personalList.forEach((item: any) => {
                        const code = (item.partsCode ?? item.partCode ?? '').toString().toLowerCase().trim();
                        if (!code) return;
                        if (!personalMap[code]) personalMap[code] = [];
                        personalMap[code].push({ name: item.pickedByName, count: item.totalRemainingQuantity });
                    });
                    setrowData(stockReportList.map((row: any) => {
                        const details = personalMap[(row.partCode ?? '').toString().toLowerCase().trim()] ?? [];
                        const facilityStockDetails = (() => {
                            try { return row.facilityStockJson ? JSON.parse(row.facilityStockJson) : []; }
                            catch { return []; }
                        })();
                        return {
                            ...row,
                            facilityStockDetails,
                            personalStockDetails: details,
                            personalStock: details.reduce((sum: number, p: any) => sum + (p.count || 0), 0),
                        };
                    }));
                },
                () => {
                    // On personal stock error, still show main data without personal details
                    setrowData(stockReportList.map((row: any) => {
                        const facilityStockDetails = (() => {
                            try { return row.facilityStockJson ? JSON.parse(row.facilityStockJson) : []; }
                            catch { return []; }
                        })();
                        return { ...row, facilityStockDetails, personalStockDetails: [], personalStock: 0 };
                    }));
                }
            );
        }, (error: any) => {
            console.log('error =>', error);
            toast.error(error);
        });

    };

    useEffect(() => {
        if (filtersInitialized) {
            GetStockInventoryBalanceReportList();
        }
        
        const hasActiveFilters = filterConfig.some((filter) => filters[filter.key]);
        setFiltersApplied({
            generalFilter: hasActiveFilters,
        });
    }, [currentPage, searchParam, filters, filtersInitialized]);

    const ExportStockInventoryBalanceReportExcel = async () => {
        const payload: any = {
            pageSize: 0,
        };
        if (filters) {
            Object.keys(filters).map((key: any) => {
                if (filters[key]) {
                    payload[key] = filters[key];
                }
            });
        }
        
        setIsExporting(true);
        spearPartsAPI.exportReportToXLS(
            payload,
            (resp: any) => {
                console.log("resp FFFFFFFFFFFFFFFFFFFFFFFF =>", resp);
                const link = document.createElement('a');
                link.href = resp.fileUrl;
                link.download = `StockInventoryBalanceReport_${moment().format('YYYY-MM-DD')}.xlsx`;
                document.body.appendChild(link);
                link.click();
                //document.body.removeChild(link);
                setIsExporting(false);
            },
            (error: any) => {
                console.log("error =>", error);
                toast.error(error);
                setIsExporting(false);
            }
        );
    };

    return (
        <Layout>
            <Header title="Stock Inventory Balance Report List" back="/reports" >
                <Header.Right>
                    {/* Excel export button is intentionally excluded as requested */}
                    <Button 
                        theme='primary' 
                        onClick={ExportStockInventoryBalanceReportExcel}
                        disabled={isExporting}
                    >
                        {isExporting ? 'Exporting...' : 'Export Excel'}
                    </Button>
                </Header.Right>
            </Header>
            <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
                <Pagination
                    perPage={PAGE_SIZE}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
                {/* <Search onSearch={setSearchParam} placeholder='Search by part name, code or transaction ID' /> */}
            </div>
            <div className='bg-[#FAFAFA]'>
                <div className='flex gap-4 px-5 py-2 items-center'>
                    <Button theme='light' onClick={() => filtersRef.current.showModal()}>Filter <Icon name='filter' size={1.40} className={filtersApplied.generalFilter ? "text-blue-700" : ""} /></Button>
                    <p className='text-primary font-semibold text-sm cursor-pointer' onClick={() => { 
                        setFilters({});
                        localStorage.removeItem('stockInventoryBalanceReportFilters');
                    }} >Clear Filters</p>
                </div>

                <div className="ag-theme-alpine px-4 w-full h-1/2">
                    <AgGridReact
                        columnDefs={columnDefs as any} // Pass columnDefs
                        rowData={rowData || []} // Pass rowData
                        rowSelection="multiple"
                        onGridReady={onGridReady}
                        pagination={false}
                        domLayout="autoHeight"
                        onRowClicked={onRowClicked}
                        loadingOverlayComponent={Loader}
                        noRowsOverlayComponent={() => (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center text-gray-500">
                                    <div className="text-lg font-medium">No Data Found</div>
                                </div>
                            </div>
                        )}
                    />
                </div>
            </div>

            <Filters _ref={filtersRef} config={filterConfig} filters={filters} onUpdate={setFilters} />
        </Layout>
    );
};
export default StockInventoryBalanceReportList;

